export interface Rgb {
  r: number;
  g: number;
  b: number;
}

export function dominantTint(data: ArrayLike<number>): Rgb | null {
  let totalWeight = 0;
  let r = 0;
  let g = 0;
  let b = 0;
  for (let i = 0; i + 3 < data.length; i += 4) {
    const alpha = data[i + 3] / 255;
    if (alpha < 0.5) continue;
    const pr = data[i];
    const pg = data[i + 1];
    const pb = data[i + 2];
    const max = Math.max(pr, pg, pb);
    const min = Math.min(pr, pg, pb);
    const saturation = max === 0 ? 0 : (max - min) / max;
    const brightness = max / 255;
    const weight = 0.08 + saturation * saturation * (0.35 + brightness);
    r += pr * weight;
    g += pg * weight;
    b += pb * weight;
    totalWeight += weight;
  }
  if (totalWeight === 0) return null;
  return { r: Math.round(r / totalWeight), g: Math.round(g / totalWeight), b: Math.round(b / totalWeight) };
}

function toHsl({ r, g, b }: Rgb) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  return { h: h * 60, s, l };
}

function fromHsl(h: number, s: number, l: number): Rgb {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [r1, g1, b1] =
    h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return { r: Math.round((r1 + m) * 255), g: Math.round((g1 + m) * 255), b: Math.round((b1 + m) * 255) };
}

export interface TintPalette {
  glow: string;
  surface: string;
  edge: string;
}

export function tintPalette(color: Rgb): TintPalette {
  const { h, s } = toHsl(color);
  const glowSaturation = Math.min(0.85, Math.max(0.35, s));
  const glow = fromHsl(h, glowSaturation, 0.52);
  const surface = fromHsl(h, Math.min(0.55, s), 0.16);
  const edge = fromHsl(h, Math.min(0.7, Math.max(0.25, s)), 0.62);
  return {
    glow: `rgb(${glow.r} ${glow.g} ${glow.b})`,
    surface: `rgb(${surface.r} ${surface.g} ${surface.b})`,
    edge: `rgb(${edge.r} ${edge.g} ${edge.b})`,
  };
}
